import { VARIABLE_TYPE } from '@idc-forms/formula/types'
import { isSensitiveToken } from './sensitive-tokens'
import { type DeclaredCatalog, VARIABLE_SCOPE, type VariableMetadata } from './variable'

/**
 * LA ASERCIÓN DE INTEGRIDAD del catálogo, que se muda con el módulo (ADR 0036).
 *
 * Vivía en `registry.ts:45` del front, corría sólo en DEV y sólo sobre el catálogo de documentos.
 * Acá es una función pura sobre metadatos: la puede llamar la aserción de DEV del front, el test
 * del paquete que corre en CI, o cualquiera que declare un catálogo nuevo.
 *
 * **No lanza.** Devuelve los problemas y el llamador decide: el front lanza al cargar para que un
 * dev vea el error al instante; el CI falla el test. Un `throw` acá obligaría a los dos a lo mismo.
 *
 * ⛔ Lo que NO valida es lo que este paquete no puede saber: si hay un `resolve` colgado del
 * metadato, o si el host inyectó el dominio que `optionsFromHost` promete. Eso lo valida el
 * adaptador, que es quien tiene el dato.
 */

const VALID_TYPES: ReadonlySet<string> = new Set<string>(Object.values(VARIABLE_TYPE))
const VALID_SCOPES: ReadonlySet<string> = new Set<string>(Object.values(VARIABLE_SCOPE))

/** Los problemas de UN catálogo. Lista vacía = está sano. */
export const checkCatalogIntegrity = (catalog: DeclaredCatalog): string[] => {
  const problems: string[] = []
  const seen = new Set<string>()
  const where = (key: string): string => `[${catalog.name}] "${key}"`

  for (const variable of catalog.variables ?? []) {
    if (!variable.key) {
      problems.push(`[${catalog.name}] Variable sin \`key\`.`)
      continue
    }
    if (seen.has(variable.key)) problems.push(`${where(variable.key)}: key DUPLICADA.`)
    seen.add(variable.key)

    // La frontera de seguridad. Es la única que habla de qué se puede exponer, no de qué está bien
    // formado, y por eso el mensaje dice el porqué y no sólo el qué.
    if (isSensitiveToken(variable.key)) {
      problems.push(
        `${where(variable.key)}: variable SENSIBLE bloqueada. No se exponen usuarios/credenciales/auth/2FA/secretos.`
      )
    }

    if (!variable.label) problems.push(`${where(variable.key)}: sin \`label\`.`)
    if (!VALID_TYPES.has(variable.type)) {
      problems.push(`${where(variable.key)}: tipo inválido "${variable.type}".`)
    }

    // Una variable sin scope no aplica en ningún lado, o sea que no existe. Es el error que se
    // comete al agregar una variable copiando otra y borrando de más.
    const scopes = variable.scopes ?? []
    if (scopes.length === 0) problems.push(`${where(variable.key)}: sin \`scopes\`.`)
    for (const scope of scopes) {
      if (!VALID_SCOPES.has(scope)) {
        problems.push(`${where(variable.key)}: scope inválido "${scope}".`)
      }
    }

    problems.push(...checkClosedDomain(variable, where(variable.key)))
  }

  return problems
}

/**
 * El dominio cerrado, con sus dos formas de declararse y la contradicción prohibida.
 *
 * `options` y `optionsFromHost` son EXCLUYENTES: declarar las dos es decir que el dominio está acá
 * y que además lo trae el host, y ese día una de las dos listas gana en silencio.
 */
const checkClosedDomain = (variable: VariableMetadata, where: string): string[] => {
  const problems: string[] = []

  if (variable.options && variable.optionsFromHost) {
    problems.push(`${where}: declara \`options\` Y \`optionsFromHost\`. Es una o la otra.`)
  }
  if (variable.options && variable.options.length === 0) {
    problems.push(`${where}: \`options\` vacío. Un dominio cerrado sin opciones no cierra nada.`)
  }
  // ⚠️ `enum` está en desuso (ADR 0036 §3) pero mientras exista tiene que traer su dominio: un
  // `enum` sin opciones deja el selector de la condición ofreciendo un texto libre, y un valor
  // tipeado a mano es una rama que no se cumple nunca.
  if (variable.type === VARIABLE_TYPE.ENUM && !variable.options && !variable.optionsFromHost) {
    problems.push(`${where}: es \`enum\` pero no declara \`options\` ni \`optionsFromHost\`.`)
  }

  return problems
}

/** Los problemas de VARIOS catálogos, en una sola lista. */
export const checkCatalogsIntegrity = (catalogs: readonly DeclaredCatalog[]): string[] =>
  catalogs.flatMap(checkCatalogIntegrity)

/** Lanza si alguno viola una invariante. Para la aserción de DEV del front. */
export const assertCatalogsIntegrity = (catalogs: readonly DeclaredCatalog[]): void => {
  const problems = checkCatalogsIntegrity(catalogs)
  if (problems.length > 0) {
    throw new Error(
      `[variables] Los catálogos violan ${problems.length} invariante(s):\n  - ${problems.join('\n  - ')}`
    )
  }
}
